import React from 'react';
import _findIndex from 'lodash/findIndex';
import _reduce from 'lodash/reduce';

import useToggle from '@lyearn/core/hooks/useToggle';
import { updateIn } from '@lyearn/core/utils/immutable';
import { useTranslation } from '@lyearn/i18n';
import { Close, MoreVerticalFill, Trash2Outline } from '@lyearn/icons';
import { ActionMenu, ActionMenuItemType, AddBlock } from '@lyearn/molecules';
import { LinkButton, Switch } from '@lyearn/ui';
import * as ContentList from '@/components/ContentList';
import useContents from '@/modules/corporate/hooks/useContents';
import { ALL_CONTENT_TYPES } from '@/modules/corporate/modules/assignment/views/AssignmentCreator/components/ContentAndCollectionList/utils';
import ContentItemContainer from '@/modules/corporate/modules/path/views/PathEditor/components/ContentItemContainer';
import SelectContentsDialog from '@/modules/corporate/modules/path/views/PathEditor/components/SelectContentsDialog';
import { ContentEntity } from '@/types/schema';

import { AddContentProps } from './types';

const OVERRIDES = {
  PopoverButton: {
    classes: { open: 'opacity-100', closed: 'opacity-0 group-hover:opacity-100' },
  },
};

const getActions =
  (
    t: (key: string) => string,
    deleteText: string | undefined,
    idVsOptionalMap: { [key: string]: boolean },
    onAction: (actionType: string, content: ContentEntity) => void,
  ) =>
  (content: ContentEntity): ActionMenuItemType[] => {
    return [
      {
        id: 'optional',
        type: 'item',
        label: t('Session:MarkAsOptional'),
        EndIcon: (props) => (
          <Switch
            {...props}
            checked={!!idVsOptionalMap[content.id]}
            onChange={() => onAction('optional', content)}
          />
        ),
        keepOpen: true,
      },
      {
        id: 'delete',
        type: 'item',
        label: deleteText || t('Common:Actions.Delete'),
        EndIcon: Trash2Outline,
        appearance: 'danger',
      },
    ];
  };

function AddContents(props: AddContentProps) {
  const { t } = useTranslation(['Path', 'Session']);
  const { label, onChange, shareable, generatedBy, disabled, isPreWorkPostWorkRequired } = props;
  const { actionText, modal, deleteText } = label;

  const { selectedIds, idVsOptionalMap } = React.useMemo(() => {
    return _reduce(
      props.selectedValues.set.contents,
      (result, { contentId, isOptional }) => {
        result.selectedIds.push(contentId);
        result.idVsOptionalMap[contentId] = !!isOptional;
        return result;
      },
      {
        selectedIds: [] as string[],
        idVsOptionalMap: {} as { [key: string]: boolean },
      },
    );
  }, [props.selectedValues.set]);

  const [showDialog, { toggle: toggleDialog }] = useToggle(false);
  const { contents: contentListItems } = useContents({
    ids: selectedIds,
    types: ALL_CONTENT_TYPES,
    generatedBy,
  });

  const onAddContent = React.useCallback(
    (val: string[]) => {
      onChange({
        set: { contents: val.map((it) => ({ contentId: it, isOptional: !!idVsOptionalMap[it] })) },
      });
      toggleDialog();
    },
    [idVsOptionalMap, onChange, toggleDialog],
  );

  const onAction = React.useCallback(
    (actionType: string, content: ContentEntity) => {
      switch (actionType) {
        case 'optional':
          const index = _findIndex(
            props.selectedValues.set.contents,
            (it) => it.contentId === content.id,
          );
          const newContents = updateIn(
            props.selectedValues.set.contents,
            [index],
            ({ contentId, isOptional }) => ({ contentId, isOptional: !isOptional }),
          );
          onChange({ set: { contents: newContents } });
          break;
        case 'delete':
          const newContentIds = props.selectedValues.set.contents.filter(
            ({ contentId }) => contentId !== content.id,
          );
          onChange({ set: { contents: newContentIds } });
          break;
        default:
          break;
      }
    },
    [onChange, props.selectedValues.set.contents],
  );

  const actions = React.useMemo(
    () => getActions(t, deleteText, idVsOptionalMap, onAction),
    [t, deleteText, idVsOptionalMap, onAction],
  );

  return (
    <>
      <ContentList.Provider
        className={selectedIds.length ? 'mb-20' : ''}
        items={selectedIds.length ? contentListItems : []}>
        <ContentList.Items
          renderer={(index) => {
            const content = contentListItems[index];
            const isMandatory =
              isPreWorkPostWorkRequired &&
              selectedIds.includes(content.id) &&
              !idVsOptionalMap[content.id];
            const isOptional = !isPreWorkPostWorkRequired || !!idVsOptionalMap[content.id];
            return (
              <ContentItemContainer
                key={content.id}
                Actions={
                  disabled ? (
                    <></>
                  ) : isPreWorkPostWorkRequired ? (
                    <ActionMenu
                      items={actions(content)}
                      overrides={OVERRIDES}
                      onAction={(actionType) => onAction(actionType, content)}>
                      <LinkButton className="py-2 px-4" color={'internal'}>
                        <MoreVerticalFill />
                      </LinkButton>
                    </ActionMenu>
                  ) : (
                    <LinkButton
                      className="py-2 px-4 opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={() => onAction('delete', content)}>
                      <Close height={18} width={18} />
                    </LinkButton>
                  )
                }
                content={content}
                disabled={disabled}
                isMandatory={isMandatory}
                isOptional={isOptional}
              />
            );
          }}
        />
      </ContentList.Provider>
      <AddBlock disabled={disabled} title={actionText} onClick={toggleDialog} />
      <React.Suspense fallback={null}>
        {showDialog ? (
          <SelectContentsDialog
            contentTypes={ALL_CONTENT_TYPES}
            currentSectionContentIds={selectedIds}
            description={modal?.description}
            primaryButtonText={modal?.primaryButtonText}
            shareable={shareable}
            title={modal?.title}
            onAddContent={onAddContent}
            onClose={toggleDialog}
          />
        ) : null}
      </React.Suspense>
    </>
  );
}

export default React.memo(AddContents);
